import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Hint, Text, useResponsive } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import { BuySellOptions } from '@/common/constants';
import { useHistory } from '@/modules/core/hooks';
import useBuySellStore from '@/modules/investments/BuySell/stores/useBuySell.store';

import { useInvestmentsHoldings, useInvestor } from '../hooks';
import useIsFullLiquidation from '../hooks/useIsFullLiquidation';
import useIsModelSwitching from '../hooks/useIsModelSwitching';
import { InvestmentPaths } from '../investments.types';

import { useGetInvestorAccountId } from './hook';

const InvestmentsButtons = () => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const accountId = useGetInvestorAccountId();
  const { isFullLiquidation, isFullLiquidated } = useIsFullLiquidation({ accountId });
  const { setBuySellTabState } = useBuySellStore();
  const isModelSwitching = useIsModelSwitching(accountId);
  const { details, totalHoldingsAmount } = useInvestmentsHoldings(accountId);

  const history = useHistory();

  const {
    data: { type: investorType },
  } = useInvestor(accountId);

  const isDisabledInvestMore = useMemo(() => {
    return !details?.availableToInvest;
  }, [details?.availableToInvest]);

  const handleSell = useCallback(() => {
    window.scrollTo({ top: 0 });
    if (investorType === InvestmentPaths.SELF_DIRECTED) {
      history.push(ROUTES.SELF_DIRECTED_SELL_INVESTMENTS(accountId));
    } else if (investorType === InvestmentPaths.MANAGED) {
      history.push(ROUTES.MANAGED_SELL_INVESTMENTS(accountId));
    }
  }, [accountId, history, investorType]);

  const isEmptyHoldings = useMemo(
    () => !totalHoldingsAmount || totalHoldingsAmount === 0,
    [totalHoldingsAmount]
  );
  const handleInvestMore = useCallback(() => {
    window.scrollTo({ top: 0 });
    if (investorType === InvestmentPaths.SELF_DIRECTED) {
      history.push(ROUTES.SELF_DIRECTED_INVEST_MORE(accountId));
    } else if (investorType === InvestmentPaths.MANAGED) {
      setBuySellTabState(BuySellOptions.INVEST_MORE_FUNDS);
      history.push(`${ROUTES.BUY_SELL}?id=${accountId}`);
    }
  }, [accountId, history, investorType, setBuySellTabState]);

  const hintWrapperWidth = useMemo(() => {
    return isMobile ? '100%' : undefined;
  }, [isMobile]);
  if (isFullLiquidated) {
    return null;
  }

  return (
    <Box direction="row" justify="stretch" gap="spacing12" align="center">
      {isModelSwitching || isEmptyHoldings || isFullLiquidation ? (
        <Hint
          width={hintWrapperWidth}
          hintWidth={250}
          hintElement={
            <AppButton testId="sell-more" buttonType="secondary" fill={isMobile} width="160px" disabled>
              {t('Sell Investments')}
            </AppButton>
          }
        >
          <Text size="small" color="textOnColor" textAlign="center">
            {isFullLiquidation
              ? t(
                  'Your full portfolio liquidation is in progress. Once the sell orders are settled, you will receive your money.'
                )
              : t('Wait for completing a trade settlement to sell investments.')}
          </Text>
        </Hint>
      ) : (
        <Box fill={isMobile}>
          <AppButton
            fill={isMobile}
            testId="sell-more"
            buttonType="secondary"
            onClick={handleSell}
            width="160px"
          >
            {t('Sell Investments')}
          </AppButton>
        </Box>
      )}

      {isFullLiquidation ? (
        <Hint
          width={hintWrapperWidth}
          hintElement={
            <AppButton testId="invest-more" fill={isMobile} disabled width="150px">
              {t('Invest More')}
            </AppButton>
          }
        >
          <Text size="small" color="textOnColor" textAlign="center">
            {t('As your portfolio is being liquidated, you cannot buy new investments at this time.')}
          </Text>
        </Hint>
      ) : isModelSwitching ? (
        <Hint
          width={hintWrapperWidth}
          hintWidth={250}
          hintElement={
            <AppButton fill={isMobile} testId="invest-more" disabled width={isMobile ? undefined : '150px'}>
              {t('Invest More')}
            </AppButton>
          }
        >
          <Text size="small" color="textOnColor" textAlign="center">
            {t(
              'Additional money cannot be invested until all pending activity has settled. This may take 2-3 business days.'
            )}
          </Text>
        </Hint>
      ) : isDisabledInvestMore ? (
        <Hint
          width={hintWrapperWidth}
          hintElement={
            <AppButton fill={isMobile} testId="invest-more" width={isMobile ? undefined : '150px'} disabled>
              {t('Invest More')}
            </AppButton>
          }
        >
          <Text size="small" color="textOnColor" textAlign="center">
            {t('There is not enough money on your account.')}
          </Text>
        </Hint>
      ) : (
        <Box fill={isMobile}>
          <AppButton fill={isMobile} testId="invest-more" onClick={handleInvestMore} width="150px">
            {t('Invest More')}
          </AppButton>
        </Box>
      )}
    </Box>
  );
};
export default InvestmentsButtons;
